import type React from 'react';
import type { FrameFormat, Resolution } from '@modelcast/types';

export type HiResSourceMode = 'sequential-images' | 'livekit-video';
export type LiveKitVideoResolution = '720p' | '1080p' | '1440p' | '4k';
export type LiveKitVideoCodec = 'vp8' | 'vp9' | 'h264' | 'av1';
export type LiveKitDegradationPreference = 'maintain-resolution' | 'maintain-framerate' | 'balanced';

export interface HiResTuningSettings {
  sourceMode: HiResSourceMode;
  sequentialResolution: Resolution;
  sequentialFormat: FrameFormat;
  sequentialQuality: number;
  sequentialFrameIntervalMs: number;
  sequentialChunkSizeKb: number;
  stillCaptureFrameRate: number;
  queueProductCaptures: boolean;
  liveKitResolution: LiveKitVideoResolution;
  liveKitCodec: LiveKitVideoCodec;
  liveKitMaxBitrateMbps: number;
  liveKitMaxFramerate: number;
  liveKitSimulcast: boolean;
  liveKitAdaptiveStream: boolean;
  liveKitDynacast: boolean;
  liveKitDegradationPreference: LiveKitDegradationPreference;
}

export const DEFAULT_HI_RES_TUNING_SETTINGS: HiResTuningSettings = {
  sourceMode: 'livekit-video',
  sequentialResolution: 'native',
  sequentialFormat: 'jpeg',
  sequentialQuality: 100,
  sequentialFrameIntervalMs: 10000,
  sequentialChunkSizeKb: 64,
  stillCaptureFrameRate: 1,
  queueProductCaptures: true,
  liveKitResolution: '4k',
  liveKitCodec: 'h264',
  liveKitMaxBitrateMbps: 14,
  liveKitMaxFramerate: 6,
  liveKitSimulcast: false,
  liveKitAdaptiveStream: false,
  liveKitDynacast: false,
  liveKitDegradationPreference: 'maintain-resolution',
};

interface HiResTuningPanelProps {
  settings: HiResTuningSettings;
  onChange: (updates: Partial<HiResTuningSettings>) => void;
  onApplyLiveKitSettings: () => void;
}

export function normalizeHiResTuningSettings(value: unknown): HiResTuningSettings {
  if (!value || typeof value !== 'object') return DEFAULT_HI_RES_TUNING_SETTINGS;
  const parsed = value as Partial<HiResTuningSettings>;

  return {
    ...DEFAULT_HI_RES_TUNING_SETTINGS,
    ...parsed,
    sourceMode: 'livekit-video',
    liveKitSimulcast: false,
    liveKitAdaptiveStream: false,
    liveKitDynacast: false,
    sequentialQuality: clampNumber(parsed.sequentialQuality, 1, 100, DEFAULT_HI_RES_TUNING_SETTINGS.sequentialQuality),
    sequentialFrameIntervalMs: clampNumber(parsed.sequentialFrameIntervalMs, 250, 60000, DEFAULT_HI_RES_TUNING_SETTINGS.sequentialFrameIntervalMs),
    sequentialChunkSizeKb: clampNumber(parsed.sequentialChunkSizeKb, 16, 512, DEFAULT_HI_RES_TUNING_SETTINGS.sequentialChunkSizeKb),
    stillCaptureFrameRate: clampNumber(parsed.stillCaptureFrameRate, 0.2, 5, DEFAULT_HI_RES_TUNING_SETTINGS.stillCaptureFrameRate),
    liveKitMaxBitrateMbps: clampNumber(parsed.liveKitMaxBitrateMbps, 1, 150, DEFAULT_HI_RES_TUNING_SETTINGS.liveKitMaxBitrateMbps),
    liveKitMaxFramerate: clampNumber(parsed.liveKitMaxFramerate, 1, 60, DEFAULT_HI_RES_TUNING_SETTINGS.liveKitMaxFramerate),
  };
}

function clampNumber(value: unknown, min: number, max: number, fallback: number) {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return Math.min(max, Math.max(min, numericValue));
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] text-text-dim mb-1">{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  min,
  max,
  step = 1,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(event) => onChange(Number(event.target.value))}
      className="w-full rounded-md border border-line bg-panel2 px-2 py-1.5 text-[12px] text-text"
    />
  );
}

function Select<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as T)}
      className="w-full rounded-md border border-line bg-panel2 px-2 py-1.5 text-[12px] text-text"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-md border border-line bg-panel2 px-2 py-1.5">
      <span className="text-[12px] text-text">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-text"
      />
    </label>
  );
}

export function HiResTuningPanel({
  settings,
  onChange,
  onApplyLiveKitSettings,
}: HiResTuningPanelProps) {
  return (
    <div className="border-t border-line p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] text-text-faint uppercase">
            Hi-Res Tuning
          </div>
          <p className="mt-1 text-[11px] leading-snug text-text-dim">
            Operator-only local overrides
          </p>
        </div>
        <span className="rounded-full border border-line bg-panel2 px-2 py-1 text-[10px] uppercase text-text-dim">
          {settings.sourceMode === 'sequential-images' ? 'Images' : 'Live'}
        </span>
      </div>

      <div className="space-y-3">
        <div className="space-y-2">
          <div className="text-[10px] font-semibold uppercase text-text-faint">
            Still Captures
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Resolution">
              <Select
                value={settings.sequentialResolution}
                onChange={(value) => onChange({ sequentialResolution: value })}
                options={[
                  { value: 'native', label: 'Native' },
                  { value: '4k', label: '4K' },
                  { value: '1080p', label: '1080p' },
                  { value: '720p', label: '720p' },
                ]}
              />
            </Field>
            <Field label="Format">
              <Select
                value={settings.sequentialFormat}
                onChange={(value) => onChange({ sequentialFormat: value })}
                options={[
                  { value: 'jpeg', label: 'JPEG' },
                  { value: 'webp', label: 'WebP' },
                  { value: 'avif', label: 'AVIF' },
                ]}
              />
            </Field>
            <Field label="Quality">
              <NumberInput
                value={settings.sequentialQuality}
                min={1}
                max={100}
                onChange={(value) => onChange({ sequentialQuality: value })}
              />
            </Field>
            <Field label="Interval ms">
              <NumberInput
                value={settings.sequentialFrameIntervalMs}
                min={250}
                max={60000}
                step={250}
                onChange={(value) => onChange({ sequentialFrameIntervalMs: value })}
              />
            </Field>
            <Field label="Chunk KB">
              <NumberInput
                value={settings.sequentialChunkSizeKb}
                min={16}
                max={512}
                step={16}
                onChange={(value) => onChange({ sequentialChunkSizeKb: value })}
              />
            </Field>
            <Field label="Still FPS">
              <NumberInput
                value={settings.stillCaptureFrameRate}
                min={0.2}
                max={5}
                step={0.2}
                onChange={(value) => onChange({ stillCaptureFrameRate: value })}
              />
            </Field>
          </div>
          <Toggle
            label="Queue product captures"
            checked={settings.queueProductCaptures}
            onChange={(checked) => onChange({ queueProductCaptures: checked })}
          />
        </div>

        <div className="space-y-2">
          <div className="text-[10px] font-semibold uppercase text-text-faint">
            LiveKit Feed
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Resolution">
              <Select
                value={settings.liveKitResolution}
                onChange={(value) => onChange({ liveKitResolution: value })}
                options={[
                  { value: '1440p', label: '1440p' },
                  { value: '4k', label: '4K' },
                  { value: '1080p', label: '1080p' },
                  { value: '720p', label: '720p' },
                ]}
              />
            </Field>
            <Field label="Codec">
              <Select
                value={settings.liveKitCodec}
                onChange={(value) => onChange({ liveKitCodec: value })}
                options={[
                  { value: 'vp9', label: 'VP9' },
                  { value: 'vp8', label: 'VP8' },
                  { value: 'h264', label: 'H.264' },
                  { value: 'av1', label: 'AV1' },
                ]}
              />
            </Field>
            <Field label="Bitrate Mbps">
              <NumberInput
                value={settings.liveKitMaxBitrateMbps}
                min={1}
                max={100}
                onChange={(value) => onChange({ liveKitMaxBitrateMbps: value })}
              />
            </Field>
            <Field label="Max FPS">
              <NumberInput
                value={settings.liveKitMaxFramerate}
                min={1}
                max={60}
                onChange={(value) => onChange({ liveKitMaxFramerate: value })}
              />
            </Field>
          </div>
          <Field label="Degrade">
            <Select
              value={settings.liveKitDegradationPreference}
              onChange={(value) => onChange({ liveKitDegradationPreference: value })}
              options={[
                { value: 'maintain-resolution', label: 'Maintain resolution' },
                { value: 'maintain-framerate', label: 'Maintain framerate' },
                { value: 'balanced', label: 'Balanced' },
              ]}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Toggle
              label="Simulcast"
              checked={settings.liveKitSimulcast}
              onChange={(checked) => onChange({ liveKitSimulcast: checked })}
            />
            <Toggle
              label="Adaptive"
              checked={settings.liveKitAdaptiveStream}
              onChange={(checked) => onChange({ liveKitAdaptiveStream: checked })}
            />
            <Toggle
              label="Dynacast"
              checked={settings.liveKitDynacast}
              onChange={(checked) => onChange({ liveKitDynacast: checked })}
            />
          </div>
          <button
            type="button"
            onClick={onApplyLiveKitSettings}
            className="w-full rounded-md border border-line bg-panel2 px-3 py-2 text-[12px] font-medium text-text hover:border-text transition-colors"
          >
            Reconnect With LiveKit Settings
          </button>
        </div>
      </div>
    </div>
  );
}
